import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { FilterAction } from '@/types/SearchFacet';
import { getMergedRangeFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import {
    modifyParamsRemovePage,
    modifyParamsRemoveParamByFacetId,
    modifyParamsWithRange,
    parseQueryParams,
} from '@/utils/queryParams';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import FacetTypes from '@/types/search/enums/facetTypes';

type FormData = {
    max: number | string;
    min: number | string;
};

type Props = {
    isArchivedSearch: boolean;
};

export const CurrentBidFilter = ({ isArchivedSearch }: Props) => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const facetId = isArchivedSearch ? FacetTypes.HAMMER_PRICE_ID : FacetTypes.CURRENT_BID_ID;
    const currentBidFacet = useAppSelector((state) => getMergedRangeFacet(state, isArchivedSearch, facetId));
    const { submitted: isLoadingSearchResults } = useAppSelector(getSearchUiState);

    const {
        formState: { errors },
        handleSubmit,
        register,
        watch,
    } = useForm<FormData>({
        defaultValues: {
            max: currentBidFacet?.range?.max === 0 ? '' : (currentBidFacet?.range?.max ?? ''),
            min: currentBidFacet?.range?.min === 0 ? '' : (currentBidFacet?.range?.min ?? ''),
        },
    });

    const formWatch = watch();

    const onCurrentBidRangeChanged = useCallback(
        ({ max, min }: FormData) => {
            let newParams = modifyParamsWithRange(queryParams, facetId, Number(min), Number(max));
            if (min === 0 && max === 0) {
                newParams = modifyParamsRemoveParamByFacetId(newParams, facetId);
            }

            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams({
                currentOpenMenu: CurrentOpenMenu.AllFilters,
                filterAction:
                    min === 0 && max === 0
                        ? isArchivedSearch
                            ? FilterAction.HammerPriceRemoved
                            : FilterAction.CurrentBidRemoved
                        : isArchivedSearch
                          ? FilterAction.HammerPriceChanged
                          : FilterAction.CurrentBidChanged,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [queryParams, facetId, navigateWithParams, isArchivedSearch]
    );

    // RESET FORM WHEN INPUTS ARE EMPTY AND CURRENT BID FACET IS NOT EMPTY
    useEffect(() => {
        if (currentBidFacet?.range?.max !== 0 && formWatch.max === '' && formWatch.min === '') {
            onCurrentBidRangeChanged({ max: 0, min: 0 });
        }
        // callback and facet are excluded from dependencies to prevent infinite loop
        // updating URL triggers server response with new facet data, which would re-run the effect indefinitely.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formWatch.max, formWatch.min]);

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={
                isArchivedSearch
                    ? formatMessage({ id: 'search_facet_title_hammerPrice' })
                    : formatMessage({ id: 'search_facet_title_currentBid' })
            }
            open
        >
            <form
                className="grid grid-cols-[1fr_min-content_1fr_min-content] items-start gap-xs"
                onSubmit={handleSubmit(onCurrentBidRangeChanged)}
            >
                <TextInput
                    {...register('min', { required: true })}
                    disabled={isLoadingSearchResults}
                    labelText="Min"
                    placeholder="$1"
                    prefix={formWatch.min ? '$' : ''}
                    small
                    type="number"
                />
                <BodyPrimary className="mt-md">-</BodyPrimary>
                <TextInput
                    {...register('max', {
                        required: true,
                        validate: (value) => Number(value) >= Number(formWatch.min),
                    })}
                    disabled={isLoadingSearchResults}
                    errorText="Please enter a valid range"
                    hasError={Boolean(errors.max)}
                    labelText="Max"
                    placeholder="$100"
                    prefix={formWatch.max ? '$' : ''}
                    small
                    type="number"
                />
                <Button
                    className="mr-[1px] mt-[18px]"
                    loading={isLoadingSearchResults}
                    size="sm"
                    type="submit"
                    variant="outlined"
                >
                    {formatMessage({ id: 'searchPage.facets.applyFilter' })}
                </Button>
            </form>
        </Accordion>
    );
};
